// Phase 4 Step #10:
// Browser fetch helper for POST /api/generate, /api/validate, and
// /api/fa-agent/verify.
//
// The browser sends FlowContext / GenerationIR to the server which performs
// AI generation (with redaction) or returns a local fallback. The server is
// the boundary that holds OPENAI_API_KEY / SUPABASE_SERVICE_ROLE_KEY — this
// module never touches secrets.
//
// Errors are typed so callers can branch on common cases:
//   GenerationApiError      - base class for any 4xx/5xx coming back as JSON
//   DailyLimitExceededError - 429 daily_limit_exceeded
//   PayloadTooLargeError    - 413 payload_too_large
//   AuthRequiredError       - 401 unauthorized

const DEFAULT_BASE_URL = readBaseUrl();

function readBaseUrl() {
    const value = import.meta.env?.VITE_API_BASE_URL;
    if (typeof value !== "string") return "";
    // Strip trailing slash for consistent concatenation.
    return value.replace(/\/+$/, "");
}

export class GenerationApiError extends Error {
    constructor(message, { status, code, details } = {}) {
        super(message);
        this.name = "GenerationApiError";
        this.status = typeof status === "number" ? status : null;
        this.code = code ?? null;
        this.details = details ?? null;
    }
}

export class DailyLimitExceededError extends GenerationApiError {
    constructor(message, options) {
        super(message, options);
        this.name = "DailyLimitExceededError";
    }
}

export class PayloadTooLargeError extends GenerationApiError {
    constructor(message, options) {
        super(message, options);
        this.name = "PayloadTooLargeError";
    }
}

export class AuthRequiredError extends GenerationApiError {
    constructor(message, options) {
        super(message, options);
        this.name = "AuthRequiredError";
    }
}

// Some HTML option values use "mitsubishi-gxworks3" while the server
// allowlist uses "mitsubishi-gx-works3". Map known mismatches at the
// boundary so the HTML can stay as-is for now. Unmapped values are passed
// through and validated by the server.
const HTML_TO_SERVER_VENDOR = {
    "mitsubishi-gxworks3": "mitsubishi-gx-works3"
};

export function mapBrowserVendorToServer(value) {
    if (typeof value !== "string") return value;
    return HTML_TO_SERVER_VENDOR[value] ?? value;
}

function buildHeaders(accessToken) {
    const headers = { "content-type": "application/json" };
    if (accessToken) headers.authorization = `Bearer ${accessToken}`;
    return headers;
}

function pickError(status, body) {
    const code = typeof body?.error === "string" ? body.error : null;
    const message =
        typeof body?.message === "string" && body.message
            ? body.message
            : code || `HTTP ${status}`;

    if (status === 401) return new AuthRequiredError(message, { status, code, details: body });
    if (status === 413) return new PayloadTooLargeError(message, { status, code, details: body });
    if (status === 429 && code === "daily_limit_exceeded") {
        return new DailyLimitExceededError(message, { status, code, details: body });
    }
    return new GenerationApiError(message, { status, code, details: body });
}

// Posts a generation request and returns the parsed response body on 200.
// Throws a typed error otherwise. Network failures throw the underlying
// fetch error so callers can choose to fall back to local templates.
export async function postGenerate(payload, { accessToken, signal, baseUrl } = {}) {
    const root = typeof baseUrl === "string" ? baseUrl.replace(/\/+$/, "") : DEFAULT_BASE_URL;
    const url = `${root}/api/generate`;

    const body = {
        ...payload,
        vendor: mapBrowserVendorToServer(payload?.vendor)
    };

    const response = await fetch(url, {
        method: "POST",
        headers: buildHeaders(accessToken),
        body: JSON.stringify(body),
        signal
    });

    let parsed = null;
    try {
        parsed = await response.json();
    } catch {
        parsed = null;
    }

    if (!response.ok) {
        throw pickError(response.status, parsed);
    }
    return parsed;
}

// Posts a deterministic validation request and returns the parsed response body
// on 200. Errors follow the same typed contract as postGenerate().
export async function postValidate(payload, { accessToken, signal, baseUrl } = {}) {
    const root = typeof baseUrl === "string" ? baseUrl.replace(/\/+$/, "") : DEFAULT_BASE_URL;
    const url = `${root}/api/validate`;

    const body = {
        ...payload,
        vendor: mapBrowserVendorToServer(payload?.vendor)
    };

    const response = await fetch(url, {
        method: "POST",
        headers: buildHeaders(accessToken),
        body: JSON.stringify(body),
        signal
    });

    let parsed = null;
    try {
        parsed = await response.json();
    } catch {
        parsed = null;
    }

    if (!response.ok) {
        throw pickError(response.status, parsed);
    }
    return parsed;
}

// Posts an fa-agent verification request and returns the parsed response body
// on 200. Errors follow the same typed contract as postGenerate().
export async function postFaAgentVerify(payload, { accessToken, signal, baseUrl } = {}) {
    const root = typeof baseUrl === "string" ? baseUrl.replace(/\/+$/, "") : DEFAULT_BASE_URL;
    const url = `${root}/api/fa-agent/verify`;

    const response = await fetch(url, {
        method: "POST",
        headers: buildHeaders(accessToken),
        body: JSON.stringify(payload || {}),
        signal
    });

    let parsed = null;
    try {
        parsed = await response.json();
    } catch {
        parsed = null;
    }

    if (!response.ok) {
        throw pickError(response.status, parsed);
    }
    return parsed;
}

export async function getFaAgentVerifyStatus(verificationId, { accessToken, signal, baseUrl } = {}) {
    const root = typeof baseUrl === "string" ? baseUrl.replace(/\/+$/, "") : DEFAULT_BASE_URL;
    const encodedId = encodeURIComponent(String(verificationId || "").trim());
    const url = `${root}/api/fa-agent/verify/${encodedId}`;

    const response = await fetch(url, {
        method: "GET",
        headers: accessToken ? { authorization: `Bearer ${accessToken}` } : {},
        signal
    });

    let parsed = null;
    try {
        parsed = await response.json();
    } catch {
        parsed = null;
    }

    if (!response.ok) {
        throw pickError(response.status, parsed);
    }
    return parsed;
}
